import { useEffect, useState } from "react";
import { api, fmtCurrency } from "../../lib/api";
import MobileHeader from "../../components/MobileHeader";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Plus, Receipt } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { toast } from "sonner";

export default function Invoices() {
    const [filter, setFilter] = useState("all");
    const [invoices, setInvoices] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ customer_name: "", amount: "" });
    const [busy, setBusy] = useState(false);

    const refresh = () => api.get("/business/invoices").then((r) => setInvoices(r.data));
    useEffect(() => { refresh(); }, []);

    const filtered = invoices.filter((i) => filter === "all" ? true : i.status === filter);

    const submit = async () => {
        const a = parseFloat(form.amount);
        if (!form.customer_name || !a) return toast.error("Customer and amount required");
        setBusy(true);
        try {
            await api.post("/business/invoices", { customer_name: form.customer_name, amount: a, items: [{ name: "Service", qty: 1, price: a }] });
            toast.success("Invoice created");
            setOpen(false);
            setForm({ customer_name: "", amount: "" });
            refresh();
        } catch (err) {
            toast.error("Failed");
        } finally {
            setBusy(false);
        }
    };

    const markPaid = async (id) => {
        try {
            await api.post(`/business/invoices/${id}/mark-paid`);
            toast.success("Marked paid");
            refresh();
        } catch (err) {
            toast.error("Failed");
        }
    };

    const COLORS = { unpaid: "#FF0055", paid: "#00D084", overdue: "#FF6B00" };

    return (
        <div data-testid="invoices-page">
            <MobileHeader title="Invoices" backTo="/business/more" variant="blue" />
            <div className="px-5 -mt-6 pb-4">
                <Tabs value={filter} onValueChange={setFilter}>
                    <TabsList className="bg-white border border-slate-200 rounded-xl p-1 grid grid-cols-4 w-full" data-testid="invoice-filter">
                        <TabsTrigger value="all" data-testid="inv-filter-all">All</TabsTrigger>
                        <TabsTrigger value="unpaid" data-testid="inv-filter-unpaid">Unpaid</TabsTrigger>
                        <TabsTrigger value="paid" data-testid="inv-filter-paid">Paid</TabsTrigger>
                        <TabsTrigger value="overdue" data-testid="inv-filter-overdue">Overdue</TabsTrigger>
                    </TabsList>
                </Tabs>

                <div className="space-y-2 mt-4">
                    {filtered.length === 0 && <div className="text-center py-12 text-sm text-slate-500">No invoices</div>}
                    {filtered.map((inv) => (
                        <div key={inv.id} data-testid={`invoice-${inv.id}`} className="bg-white rounded-2xl p-4 border border-slate-200/60 product-shadow">
                            <div className="flex items-center gap-3">
                                <div className="size-10 rounded-xl flex items-center justify-center" style={{ background: `${COLORS[inv.status]}15`, color: COLORS[inv.status] }}>
                                    <Receipt size={18} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="font-semibold text-sm">{inv.number}</div>
                                    <div className="text-xs text-slate-500 truncate">{inv.customer_name}</div>
                                </div>
                                <div className="text-right">
                                    <div className="font-display font-bold text-sm">{fmtCurrency(inv.amount)}</div>
                                    <div className="text-[10px] uppercase font-bold tracking-wider mt-0.5" style={{ color: COLORS[inv.status] }}>
                                        {inv.status}
                                    </div>
                                </div>
                            </div>
                            {inv.status === "unpaid" && (
                                <Button onClick={() => markPaid(inv.id)} variant="outline" className="w-full mt-3 h-9 rounded-lg text-xs" data-testid={`mark-paid-${inv.id}`}>
                                    Mark as Paid
                                </Button>
                            )}
                        </div>
                    ))}
                </div>

                <Button onClick={() => setOpen(true)} className="w-full h-12 rounded-xl bg-[#0066FF] hover:bg-[#0052cc] font-semibold mt-4" data-testid="new-invoice-btn">
                    <Plus size={18} /> New Invoice
                </Button>
            </div>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="rounded-2xl">
                    <DialogHeader><DialogTitle className="font-display">New Invoice</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <div><Label>Customer</Label><Input value={form.customer_name} onChange={(e) => setForm((f) => ({ ...f, customer_name: e.target.value }))} className="mt-1.5 h-11 rounded-xl" data-testid="inv-customer" /></div>
                        <div><Label>Amount (NGN)</Label><Input type="number" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} className="mt-1.5 h-11 rounded-xl" data-testid="inv-amount" /></div>
                        <Button onClick={submit} disabled={busy} className="w-full h-11 rounded-xl bg-[#0066FF]" data-testid="inv-submit">{busy ? "Saving…" : "Create Invoice"}</Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
